export const CHAT_UPLOAD_JSON_FALLBACK_MAX_BYTES = 15 * 1024 * 1024

export type ChatFileUploadMode = 'auto' | 'json_base64'
export type ChatFileChunkConfig = { chunk_bytes: number; max_file_bytes: number }
export type ChatFileUploadConfig = {
  mode: ChatFileUploadMode
  max_json_file_bytes: number
  chunk_upload?: ChatFileChunkConfig
}
export type ChatFileUploadEvent =
  | 'multipart_failed'
  | 'json_fallback_started'
  | 'json_fallback_succeeded'
  | 'json_fallback_failed'
  | 'json_direct_started'
  | 'json_direct_succeeded'
  | 'json_direct_failed'
export type ChatFileUploadTransport = 'multipart' | 'json_fallback' | 'json_base64' | 'json_chunks'

export const parseChatFileUploadConfig = (value: any): ChatFileUploadConfig => {
  if (
    !value ||
    (value.mode !== 'auto' && value.mode !== 'json_base64') ||
    !Number.isSafeInteger(value.max_json_file_bytes) ||
    value.max_json_file_bytes <= 0
  ) {
    throw new Error('Не удалось получить настройки загрузки файлов. Повторите попытку.')
  }
  if (
    value.chunk_upload !== undefined &&
    (!value.chunk_upload ||
      !Number.isSafeInteger(value.chunk_upload.chunk_bytes) ||
      value.chunk_upload.chunk_bytes <= 0 ||
      value.chunk_upload.chunk_bytes > 2 * 1024 * 1024 ||
      !Number.isSafeInteger(value.chunk_upload.max_file_bytes) ||
      value.chunk_upload.max_file_bytes <= 0)
  ) {
    throw new Error('Не удалось получить настройки загрузки файлов частями. Обновите страницу.')
  }
  return {
    mode: value.mode,
    max_json_file_bytes: Math.min(value.max_json_file_bytes, CHAT_UPLOAD_JSON_FALLBACK_MAX_BYTES),
    ...(value.chunk_upload ? { chunk_upload: value.chunk_upload } : {}),
  }
}

export const uploadChatFileWithTransport = async <T>(input: {
  config: ChatFileUploadConfig
  fileSize: number
  uploadMultipart: () => Promise<T>
  uploadJson: () => Promise<T>
  uploadChunks?: () => Promise<T>
  report: (event: ChatFileUploadEvent, transport: ChatFileUploadTransport, error?: any) => void
}): Promise<T> => {
  const config = parseChatFileUploadConfig(input.config)
  const uploadJson = async (direct: boolean) => {
    const chunked = direct && Boolean(config.chunk_upload)
    const maxBytes = chunked ? config.chunk_upload!.max_file_bytes : config.max_json_file_bytes
    const transport = chunked ? 'json_chunks' : direct ? 'json_base64' : 'json_fallback'
    try {
      if (input.fileSize === 0) throw new Error('Файл пуст. Выберите файл с содержимым.')
      if (
        !Number.isSafeInteger(input.fileSize) ||
        input.fileSize < 0 ||
        input.fileSize > maxBytes
      ) {
        throw new Error(
          `В режиме JSON/Base64 можно загрузить файл до ${Math.floor(
            maxBytes / 1024 / 1024,
          )} МБ. Уменьшите файл или обратитесь к администратору.`,
        )
      }
      if (chunked && !input.uploadChunks)
        throw new Error('Обновите страницу для загрузки файла частями.')
      input.report(direct ? 'json_direct_started' : 'json_fallback_started', transport)
      const result = await (chunked ? input.uploadChunks!() : input.uploadJson())
      input.report(direct ? 'json_direct_succeeded' : 'json_fallback_succeeded', transport)
      return result
    } catch (error) {
      input.report(direct ? 'json_direct_failed' : 'json_fallback_failed', transport, error)
      throw error
    }
  }

  if (config.mode === 'json_base64') return uploadJson(true)
  try {
    return await input.uploadMultipart()
  } catch (error) {
    input.report('multipart_failed', 'multipart', error)
    if (
      !shouldRetryChatUploadAsJson({
        error,
        fileSize: input.fileSize,
        maxBytes: config.max_json_file_bytes,
      })
    )
      throw error
    return uploadJson(false)
  }
}

export const chatFileUploadErrorMessage = (error: any) => {
  if (typeof error?.payload?.raw === 'string') {
    return 'Вместо ответа загрузки получен неожиданный ответ сервера или сетевого шлюза. Передайте код ошибки администратору.'
  }
  return String(error?.message || 'Не удалось загрузить файл').trim()
}

export type UploadFailureDiagnostics = {
  event_type?: string
  status_code?: number
  ready_state?: number
  loaded_bytes?: number
  total_bytes?: number
  duration_ms?: number
}

export const getUploadFailureDiagnostics = (error: any): UploadFailureDiagnostics => {
  const value = error?.payload?.upload
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as UploadFailureDiagnostics
}

export const shouldRetryChatUploadAsJson = (input: {
  error: any
  fileSize: number
  maxBytes?: number
}) => {
  const diagnostics = getUploadFailureDiagnostics(input.error)
  const maxBytes = Number.isFinite(input.maxBytes)
    ? Math.max(0, Number(input.maxBytes))
    : CHAT_UPLOAD_JSON_FALLBACK_MAX_BYTES
  return (
    Number(input.error?.statusCode || diagnostics.status_code || 0) === 0 &&
    ['error', 'timeout'].includes(String(diagnostics.event_type || '')) &&
    Number.isFinite(input.fileSize) &&
    input.fileSize >= 0 &&
    input.fileSize <= maxBytes
  )
}

export const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }
  return btoa(binary)
}

export const fileToBase64 = async (file: Blob) => arrayBufferToBase64(await file.arrayBuffer())

export const fileExtensionForDiagnostics = (name: string) => {
  const normalized = String(name || '').trim()
  const dotIndex = normalized.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex === normalized.length - 1) return ''
  return normalized
    .slice(dotIndex + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 16)
}
